/**
 * Thin client for the RFQ Orchestrator (piece C).
 *
 * Base URL comes from REACT_APP_RFQ_ORCHESTRATOR_BASE_URL (set in the local
 * .env for the POC; e.g. http://localhost:8099). C runs with USE_MOCK_BB=1
 * locally, so no auth/API key is required for these calls. The orchestrator
 * already logs every request to the dev log sink, so the calls are traced
 * end-to-end without the frontend talking to the sink directly.
 *
 * Caller auth (#rfq-caller-auth): when a BB user is logged in we attach their
 * JWT as `Authorization: Bearer <jwt>` so C + the portal can verify WHO is
 * filing/accepting an RFQ. The token comes from Redux
 * (`state.route.CLIENT_TOKEN.JWT_TOKEN` for the logged-in iframe session, else
 * `state.route.JWT_TOKEN.JWT_TOKEN`) — App.js subscribes to the store and pushes
 * it here via `setRfqAuthToken`. No token (guest/anonymous) → NO Authorization
 * header, so C's public routes (lookups, parse/draft) keep working for guests.
 * This is the SENDING side only — harmless while the receivers are ungated.
 *
 * Kept deliberately small (DRY/YAGNI): one helper per endpoint A actually uses.
 */
import axios from 'axios';

const BASE_URL = (process.env.REACT_APP_RFQ_ORCHESTRATOR_BASE_URL || '').replace(/\/$/, '');
const JSON_HEADERS = { 'Content-Type': 'application/json' };

// Module-level JWT registry. App.js keeps this in sync with the store (one
// wiring point) so every helper below — and the attachment POST that imports
// getAuthHeaders — picks up the same token without threading it through each
// call site.
let currentJwt = null;

/**
 * Set (or clear) the caller's BB JWT used for Authorization on C requests.
 * Pass a falsy value (guest/logout) to clear it.
 * @param {string|null|undefined} jwt
 */
export function setRfqAuthToken(jwt) {
  currentJwt = jwt || null;
}

/**
 * The Authorization header for the current caller, or `{}` when there is no
 * JWT (guest/anonymous). Exported so the cross-origin attachment POST (which
 * goes to the portal, not C) can attach the SAME header.
 * @returns {{Authorization?: string}}
 */
export function getAuthHeaders() {
  return currentJwt ? { Authorization: `Bearer ${currentJwt}` } : {};
}

// Shared axios instance. The request interceptor attaches the caller's JWT to
// EVERY C request when one is set, and sends NO Authorization header otherwise.
const rfqClient = axios.create();
rfqClient.interceptors.request.use((config) => {
  const auth = getAuthHeaders();
  if (auth.Authorization) {
    config.headers = { ...config.headers, ...auth };
  }
  return config;
});

export function isRfqConfigured() {
  return Boolean(BASE_URL);
}

// Single guard so every helper fails the same way when the POC env is missing.
function ensureBase() {
  if (!BASE_URL) {
    throw new Error(
      'RFQ orchestrator URL not configured (REACT_APP_RFQ_ORCHESTRATOR_BASE_URL).'
    );
  }
  return BASE_URL;
}

/**
 * POST /rfq — create an RFQ from the rate-search results.
 * @param {object} payload createRfqSchema shape (see rfq-orchestrator).
 * @returns {Promise<{rfqId:number, status:string}>}
 */
export async function createRfq(payload) {
  const { data } = await rfqClient.post(`${ensureBase()}/rfq`, payload, { headers: JSON_HEADERS });
  return data;
}

/**
 * GET /parse/draft/:id — fetch a parsed-email draft the Front plugin staged for
 * the "Open prefilled form" deep-link (#ai-parse slice 1B). Public-zone on C (no
 * key). Expired/unknown → 410/404 → returns null so the creator falls back to a
 * blank/manual form instead of erroring.
 * @returns {Promise<{intent:string, draft:object, confidence:number, uncertainFields:string[]}|null>}
 */
export async function fetchParseDraft(draftId) {
  try {
    const { data } = await rfqClient.get(`${ensureBase()}/parse/draft/${encodeURIComponent(draftId)}`);
    return data;
  } catch (err) {
    const status = err && err.response && err.response.status;
    if (status === 404 || status === 410) return null;
    throw err;
  }
}

/**
 * GET /lookups/companies — customer type-ahead for the admin "create RFQ for any
 * customer" picker (#admin-new-rfq / S-adm-1). Public on C (no key).
 * @param {string} q     name substring (empty → first page A-Z)
 * @param {number} limit page cap (C clamps to 1..50; default 20)
 * @returns {Promise<Array<{id:number, name:string}>>}
 */
export async function searchCompanies(q, limit = 20) {
  const { data } = await rfqClient.get(`${ensureBase()}/lookups/companies`, {
    params: { q: q || '', limit },
  });
  return (data && data.results) || [];
}

/**
 * POST /lookups/companies — inline "+ create new customer" from the admin picker.
 * Find-or-create by name; returns the (possibly deduped) company id.
 * @param {string} name
 * @returns {Promise<{companyId:number, name:string, created:boolean}>}
 */
export async function createCompany(name) {
  const { data } = await rfqClient.post(
    `${ensureBase()}/lookups/companies`,
    { name },
    { headers: JSON_HEADERS },
  );
  return data;
}

/**
 * GET /lookups/customers/:customerId/locations — the SELECTED/PARSED customer's
 * saved door addresses (#214). C fetches with its BB service creds scoped by the
 * customer id, so the result is the customer's own locations regardless of which
 * entity the agent is logged in / impersonating. The caller JWT rides via the
 * interceptor as the access gate. No customer yet → [] (no call).
 * @param {number|string|null} customerId
 * @returns {Promise<Array>} raw BB location rows
 */
export async function getCustomerLocations(customerId) {
  if (customerId == null || customerId === '') return [];
  const { data } = await rfqClient.get(
    `${ensureBase()}/lookups/customers/${encodeURIComponent(customerId)}/locations`,
  );
  return (data && data.results) || [];
}

/**
 * GET /lookups/customers/:customerId/purchase-orders?q= — the POs the RFQ is FOR
 * (#214), scoped server-side by customer id instead of the browser entity.
 * C returns the FULL PO field-set (same shape the old browser query returned), so
 * PoMultiSelect's commodity import (itemDescription/valueOfGoodsUsd/originCountry/
 * currency → Customs+Insurance) keeps working unchanged.
 * @param {number|string|null} customerId
 * @param {string} q  orderNumbers substring (optional)
 * @returns {Promise<Array<{id:number, orderNumbers:string, styleNo?:string,
 *   itemDescription?:string, valueOfGoodsUsd?:number, originCountry?:string, currency?:string}>>}
 */
export async function getCustomerPurchaseOrders(customerId, q) {
  if (customerId == null || customerId === '') return [];
  const { data } = await rfqClient.get(
    `${ensureBase()}/lookups/customers/${encodeURIComponent(customerId)}/purchase-orders`,
    { params: { q: q || '' } },
  );
  return (data && data.results) || [];
}

/**
 * GET /rfq/:id/quotes — the agent's PRESENTED options for the customer
 * offer-view (#78 W5). Each quote carries the sell price + per-section legs.
 * @returns {Promise<{rfqId:number, status:string, mode:string, lane:object, quotes:Array}>}
 */
export async function getRfqQuotes(rfqId) {
  const { data } = await rfqClient.get(`${ensureBase()}/rfq/${encodeURIComponent(rfqId)}/quotes`);
  return data;
}

/**
 * GET /quote/:id/record — the full Portal-quote-shaped record for one assembled
 * Quote (carrier/vessel/ports/validity ids + totals). The booking handoff overlays
 * these real ids onto the offer card before booking so the Booking references the
 * actual selected quote, not the cloned sample template (#188).
 * @returns {Promise<Object>} the Quote record (id, carrier{id}, locationTo{id}, ...)
 */
export async function getQuoteRecord(quoteId) {
  const { data } = await rfqClient.get(`${ensureBase()}/quote/${encodeURIComponent(quoteId)}/record`);
  return data;
}

/**
 * POST /quote/:id/accept — customer accepts a presented option; the RFQ flips to
 * ACCEPTED. NOTE: this marks the Quote/RFQ accepted and links a pre-existing
 * Booking if one is passed — it does NOT itself create a Booking.
 * @returns {Promise<{primeFreightRef:?string, bookingId:string, rfqId:number, quoteId:number, status:string}>}
 *   `primeFreightRef` is the REAL Booking-module reference — the authoritative
 *   field callers should trust — and is null until a real Booking is linked.
 *   `bookingId` may be a fabricated `BKG-<rfqId>-<quoteId>` placeholder when no
 *   real Booking exists, so it must NOT be shown as a confirmation number.
 */
export async function acceptQuote(quoteId, bookingId) {
  // #188: when the caller already created the REAL Booking (Create_Quote_And_Booking),
  // pass its record id so D links Quote.bookings — without it the accepted RFQ quote
  // has no booking lineage (C reads body.bookingId, D links only when present).
  const body = bookingId != null ? { bookingId } : {};
  const { data } = await rfqClient.post(`${ensureBase()}/quote/${encodeURIComponent(quoteId)}/accept`, body, { headers: JSON_HEADERS });
  return data;
}

/**
 * The authoritative booking reference from an acceptQuote response, or null.
 * Trust ONLY `primeFreightRef` (the real Booking-module ref). acceptQuote's
 * `bookingId` may be a fabricated `BKG-<rfqId>-<quoteId>` placeholder for a
 * Booking that was never created, so it must NEVER be shown to a customer as a
 * confirmation number. One rule, one place — every accept UI reads through this.
 */
export const bookingRefOf = (acceptRes) => (acceptRes && acceptRes.primeFreightRef) || null;
